import { useEffect, useState } from 'react';
import { getCompanyServices } from '../services/api';
import { calculateLeadValue } from '../utils/leadUtils';
import ServiceManager from './ServiceManager';

const ServiceSelector = ({ 
  selectedServices = [], 
  onServicesChange, 
  authToken,
  leadId,
  lead = null,
  onNewServiceAdded = null
}) => {
  const [availableServices, setAvailableServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Service management modal state
  const [showServiceManager, setShowServiceManager] = useState(false);
  const [editingService, setEditingService] = useState(null);

  // Fetch available company services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getCompanyServices(authToken);
        console.log('Fetched services response:', response); // Debug log
        
        // Handle paginated response from DRF
        let services;
        if (response && response.results && Array.isArray(response.results)) {
          services = response.results;
        } else if (Array.isArray(response)) {
          services = response;
        } else {
          console.error('Services response has unexpected format:', response);
          setAvailableServices([]);
          setError('Invalid services data received');
          return;
        }
        
        setAvailableServices(services);
      } catch (err) {
        setError('Error loading services');
        console.error('Service fetch error:', err);
        setAvailableServices([]);
      } finally {
        setLoading(false);
      }
    };

    if (authToken) {
      fetchServices();
    } else {
      // If no authToken, stop loading immediately
      setLoading(false);
    }
  }, [authToken]);

  // Group services by category
  const servicesByCategory = Array.isArray(availableServices) 
    ? availableServices.reduce((acc, service) => {
        const category = service.category || 'other';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(service);
        return acc;
      }, {})
    : {};

  // Calculate total estimated value - use persisted value with fallback
  const totalEstimatedValue = calculateLeadValue(selectedServices, lead);

  const categoryLabels = {
    preventive: 'Preventive Care',
    cosmetic: 'Cosmetic',
    restorative: 'Restorative',
    consultation: 'Consultation',
    emergency: 'Emergency',
    other: 'Other Services'
  };

  const isServiceSelected = (serviceId) => {
    if (!Array.isArray(selectedServices)) {
      console.log('selectedServices is not an array:', selectedServices);
      return false;
    }
    return selectedServices.some(s => s.service === serviceId || s.id === serviceId);
  };

  const handleServiceToggle = (service) => {
    // Ensure selectedServices is an array
    const currentServices = Array.isArray(selectedServices) ? selectedServices : [];
    let updatedServices;
    
    if (isServiceSelected(service.id)) {
      // Remove service
      updatedServices = currentServices.filter(
        s => s.service !== service.id && s.id !== service.id
      );
    } else {
      // Add service
      updatedServices = [
        ...currentServices,
        {
          service: service.id,
          service_name: service.name,
          service_category: service.category,
          service_price: service.price,
          notes: ''
        }
      ];
    }
    
    onServicesChange(updatedServices);
  };

  const handleCreateService = () => {
    setEditingService(null);
    setShowServiceManager(true);
    setShowDropdown(false);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setShowServiceManager(true);
    setShowDropdown(false);
  };

  const handleServiceSaved = async (savedService) => {
    // Refresh the available services list
    try {
      const response = await getCompanyServices(authToken);
      let services;
      if (response && response.results && Array.isArray(response.results)) {
        services = response.results;
      } else if (Array.isArray(response)) {
        services = response;
      } else {
        services = [];
      }
      setAvailableServices(services);
      
      // If this was a new service (not editing), automatically select it
      if (!editingService) {
        const currentServices = Array.isArray(selectedServices) ? selectedServices : [];
        const newServiceForLead = {
          service: savedService.id,
          service_name: savedService.name,
          service_category: savedService.category,
          service_price: savedService.price,
          notes: ''
        };
        
        // Check if service is not already selected
        if (!currentServices.find(s => s.service === savedService.id)) {
          const updatedServices = [...currentServices, newServiceForLead];
          // Use custom handler if provided, otherwise use default
          if (onNewServiceAdded) {
            onNewServiceAdded(updatedServices, savedService);
          } else {
            onServicesChange(updatedServices);
          }
        }
      }
    } catch (err) {
      console.error('Error refreshing services:', err);
    }
  };

  const handleCloseServiceManager = () => {
    setShowServiceManager(false);
    setEditingService(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading services...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selected services display */}
      {Array.isArray(selectedServices) && selectedServices.length > 0 && (
        <div className="bg-accent-teal-light-bg border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-3">Selected Services:</h4>
          <div className="flex flex-wrap gap-2">
            {selectedServices.map((selectedService, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-accent-teal-subtle text-blue-800"
              >
                {selectedService.service_name}
                <button
                  onClick={() => {
                    const service = availableServices.find(
                      (s) => s.id === selectedService.service,
                    );
                    if (service) handleServiceToggle(service);
                  }}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Estimated value display */}
      {Array.isArray(selectedServices) && selectedServices.length > 0 && (
        <div className="bg-green-100 border border-green-300 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-green-800">
              Estimated Lead Value:
            </span>
            <span className="text-2xl font-bold text-green-900">
              ${totalEstimatedValue.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Service selector dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full bg-white border border-border rounded-lg px-4 py-3 text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-300"
        >
          <div className="flex items-center justify-between">
            <span className="text-gray-700">
              {Array.isArray(selectedServices) && selectedServices.length > 0
                ? `${selectedServices.length} service${
                    selectedServices.length !== 1 ? "s" : ""
                  } selected`
                : "Select services of interest"}
            </span>
            <svg
              className={`h-5 w-5 text-gray-400 transform transition-transform ${
                showDropdown ? "rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        {showDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-sm max-h-96 overflow-y-auto">
            {/* Add New Service Button */}
            <div className="p-2 border-b border-border">
              <button
                onClick={handleCreateService}
                className="w-full flex items-center justify-center px-3 py-2 bg-accent-teal-light-bg text-blue-600 rounded-lg hover:bg-accent-teal-subtle transition-colors"
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add New Service
              </button>
            </div>

            {Object.entries(servicesByCategory).map(([category, services]) => (
              <div key={category} className="p-2">
                <h5 className="text-sm font-semibold text-gray-700 px-2 py-1 bg-background rounded mb-2">
                  {categoryLabels[category] || category}
                </h5>
                {services.map((service) => (
                  <div
                    key={service.id}
                    className="flex items-center px-2 py-2 hover:bg-background rounded group"
                  >
                    <label className="flex items-center flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isServiceSelected(service.id)}
                        onChange={() => handleServiceToggle(service)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-900 font-medium">
                            {service.name}
                          </span>
                          {service.price && (
                            <span className="text-gray-500 text-sm">
                              ${parseFloat(service.price).toFixed(0)}
                            </span>
                          )}
                        </div>
                        {service.description && (
                          <p className="text-gray-600 text-sm mt-1">
                            {service.description}
                          </p>
                        )}
                      </div>
                    </label>
                    {/* Edit Button - Only show for custom services */}
                    <button
                      onClick={() => handleEditService(service)}
                      className="ml-2 p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-all"
                      title="Edit service"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Close dropdown on outside click */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}

      {/* Service Manager Modal */}
      <ServiceManager
        authToken={authToken}
        isOpen={showServiceManager}
        onClose={handleCloseServiceManager}
        onServiceSaved={handleServiceSaved}
        editingService={editingService}
      />
    </div>
  );
};

export default ServiceSelector;